import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Col, Container, Icon, Row } from '@pinpoint/design-kit';

import EqualBucketsSelector from 'src/components/models/equal_buckets_selector';
import { bcActions, modelsCompareActions, modelViewActions } from 'src/store/actions';
import { GraphicPage } from 'src/containers';
import { Button, Message } from 'src/components';
import { parseQs } from 'src/functions';

import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';
import CompareRoiCalculator from 'src/components/models/charts/actuarial_calculator/compare';
import { ApiResponseProp, ModelProp, RoiCalcSeedProp, RoiCalcStateProp } from 'src/store/typings';

function CompareCalculators({
  history,
  location: { search },
  match: {
    params: { modelClass },
  },
  modelsCompare: { models, fetchModelsStatus, equalType, seeds },
  roiCalculatorState,
  fetchModels,
  setModelNames,
  setEqualType,
  resetPage,
  resetViewPage,
  resetRoiCalcStateField,
  updateRoiCalcState,
}) {
  const anyLegacy = models?.some((model) => model?.payload?.version === 'LIFT_VERSION_1_ACT');
  const modelIds = useMemo(() => {
    const { id: ids } = parseQs(search.replace('?', ''));
    return ids.join(',');
  }, [search]);

  const getData = useCallback(() => {
    if (modelIds?.length) fetchModels(modelIds.split(','));
  }, [fetchModels, modelIds]);

  useEffect(() => {
    if (models?.length && fetchModelsStatus === CONSTANTS.API.SUCCEEDED) {
      const names = models.map(({ name }) => name);
      setModelNames(names);
    }
  }, [models, fetchModelsStatus, setModelNames]);

  /*
   * Fetch data whenever the tenant or params change
   */
  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(
    () => () => {
      resetPage();
      resetViewPage();
    },
    [resetViewPage, resetPage]
  );

  return (
    <Container
      w="100%"
      minW="100%"
      h="100%"
      minH="100%"
      data-debug="container"
      d="flex"
      flexDir="column"
      overflow="hidden"
    >
      <Row data-debug="nav-row" p={{ t: '1.5rem' }} m={{ x: '.5rem' }}>
        <Col gutterWidth="0" d="inline-flex" size="12" align="center">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push(ROUTES.MODELS);
            }}
          >
            <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
            BACK
          </Button>
          <Button
            buttonStyle="primary"
            onClick={() => {
              const target = `${ROUTES.MODELS_COMPARE}${search}`.replace(':modelClass', modelClass);
              history.push(target);
            }}
          >
            <Icon name="BarChartEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.2rem' }} />
            <Icon name="BarChartEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.2rem' }} />
            COMPARE LIFT CHARTS
          </Button>
          <EqualBucketsSelector
            models={models}
            equalType={equalType}
            handleEqualTypeChange={setEqualType}
            disablePremiums={anyLegacy}
          />
        </Col>
      </Row>
      <Row
        data-debug="content"
        flexDir="column"
        p={{ t: '.5rem', b: '1.5rem' }}
        m={{ x: '.5rem' }}
        h="100%"
        justify="stretch"
        align="stretch"
      >
        {
          {
            [CONSTANTS.API.SUCCEEDED]: (
              <Col
                gutterWidth="0"
                bg="white"
                rounded="md"
                overflow="hidden"
                d="flex"
                flexGrow="1"
                data-test="paper"
                flexDir="row"
              >
                <CompareRoiCalculator
                  models={models}
                  calcState={roiCalculatorState}
                  equalType={equalType}
                  updateRoiCalcState={updateRoiCalcState}
                  resetRoiCalcStateField={resetRoiCalcStateField}
                  seeds={seeds}
                />
              </Col>
            ),
            [CONSTANTS.API.FAILED]: <Message type="error" />,
            [CONSTANTS.API.STARTED]: <Message />,
            default: <Message default type="info" text="No data" cta={{ text: 'Refresh', onClick: getData }} />,
          }[fetchModelsStatus]
        }
      </Row>
    </Container>
  );
}

const mapStateToProps = ({ app, bc, modelsCompare, modelView: { roiCalculatorState } }) => ({
  app,
  bc,
  modelsCompare,
  roiCalculatorState,
});

const mapDispatchToProps = (dispatch) => ({
  fetchModels: (query) => dispatch(modelsCompareActions.fetchModels(query)),
  setModelNames: (segmentNames) => dispatch(bcActions.setModelNames(segmentNames)),
  setEqualType: (eqType) => dispatch(modelsCompareActions.setEqualType(eqType)),
  setQuantiles: (quantile) => dispatch(modelsCompareActions.setQuantiles(quantile)),
  setChart: (...params) => dispatch(modelsCompareActions.setChart(...params)),
  updateRoiCalcState: (key, value) => dispatch(modelViewActions.updateRoiCalculatorState(key, value)),
  resetRoiCalcStateField: (key) => dispatch(modelViewActions.resetRoiCalculatorStateField(key)),
  resetPage: () => dispatch(modelsCompareActions.resetPage()),
  resetViewPage: () => dispatch(modelViewActions.resetPage()),
});

CompareCalculators.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string.isRequired }).isRequired,
  app: PropTypes.shape({
    tenant: PropTypes.string.isRequired,
  }).isRequired,
  modelsCompare: PropTypes.shape({
    models: PropTypes.arrayOf(ModelProp).isRequired,
    seeds: PropTypes.arrayOf(RoiCalcSeedProp).isRequired,
    fetchModelsStatus: ApiResponseProp,
    chart: PropTypes.string,
    quantiles: PropTypes.string,
    equalType: PropTypes.string,
  }).isRequired,
  roiCalculatorState: RoiCalcStateProp.isRequired,
  binsIndices: PropTypes.shape({}).isRequired,
  fetchModels: PropTypes.func.isRequired,
  setModelNames: PropTypes.func.isRequired,
  setEqualType: PropTypes.func.isRequired,
  resetPage: PropTypes.func.isRequired,
  resetViewPage: PropTypes.func.isRequired,
  resetRoiCalcStateField: PropTypes.func.isRequired,
  updateRoiCalcState: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(GraphicPage(CompareCalculators));
